/**
 * Ошибка конвейера макета (`base_designs.pipeline_error`, событие `pipeline.error`)
 * человеческим языком.
 *
 * Сервер кладёт в ошибку строку кодов, например
 * `PIPELINE_DRAFT_PREPARATION_FAILED: MARKETPLACE_DRAFT_NOT_PUBLISHABLE:
 * PL126943@ym/24: YM_CATEGORY_MISSING; PL226944@ym/24: YM_CATEGORY_MISSING`.
 * Здесь она раскладывается на группы «площадка + причина» с числом товаров и
 * подсказкой, что делать. Исходная строка сохраняется целиком: её показывают
 * свёрнутой в «Техническом тексте», потому что имя кода нужно, чтобы найти его
 * в логе и в коде.
 *
 * Тексты кодов берутся из `marketplaceReasons.ts` — отдельного словаря здесь нет.
 */

import { MARKETPLACES, normalizeMarketplace, type MarketplaceCode } from '../../../../shared/marketplaces'
import { blockingReason, isKnownReasonCode, reasonSummary } from './marketplaceReasons'

export type ErrorMarketplace = MarketplaceCode

export interface PipelineErrorGroup {
  /** `площадка|код` — стабильный ключ строки. */
  key: string
  marketplace: ErrorMarketplace | null
  /** Подпись площадки: «ВБ», «ОЗОН», «Маркет». */
  marketplaceLabel: string | null
  /** Основа кода без хвоста: `YM_CATEGORY_MISSING`. */
  code: string
  /** Фраза причины: «у категории не задана категория Яндекс Маркета». */
  short: string
  /** Что делать; null, если добавить нечего. */
  action: string | null
  /** Артикулы без повторов, в порядке появления. */
  articles: string[]
  /** Кабинеты (номера продавцов) без повторов. */
  cabinets: string[]
}

export interface PipelineErrorView {
  /** Общий заголовок: «Не удалось подготовить черновики карточек». */
  headline: string | null
  groups: PipelineErrorGroup[]
  /** Ошибка без разбора по артикулам — готовый текст. */
  plain: string | null
  /** Подсказка к `plain`, если код известен. */
  plainAction: string | null
  /** Исходная строка целиком. */
  technical: string
}


/** Обёртки, которые конвейер добавляет перед причиной; смысл несёт заголовок. */
const WRAPPER_HEADLINES: Record<string, string> = {
  PIPELINE_DRAFT_PREPARATION_FAILED: 'Не удалось подготовить черновики карточек',
}

const NOT_PUBLISHABLE = 'MARKETPLACE_DRAFT_NOT_PUBLISHABLE'
const NOT_PUBLISHABLE_HEADLINE = 'Часть карточек площадка не примет'

const CODE_START = /^[A-Z][A-Z0-9_]{2,}(?::|$)/

/**
 * Коды в записи одного артикула перечислены через запятую, но у части кодов
 * есть хвост с запятыми (`SEO_REVISION_MISSING: content_id, revision_id`).
 * Кусок, не начинающийся с кода, принадлежит предыдущему.
 */
export function splitReasonCodes(value: string): string[] {
  const result: string[] = []
  for (const piece of value.split(',')) {
    const text = piece.trim()
    if (!text) continue
    if (result.length === 0 || CODE_START.test(text)) result.push(text)
    else result[result.length - 1] = `${result[result.length - 1]}, ${text}`
  }
  return result
}

const baseOf = (code: string): string => {
  const index = code.indexOf(':')
  return (index === -1 ? code : code.slice(0, index)).trim()
}

const ENTRY = /^([^@\s:;]+)@([^/\s:;]+)\/([^:\s;]+):\s*(.+)$/

interface ParsedEntry {
  article: string
  marketplace: ErrorMarketplace | null
  rawMarketplace: string
  sellerId: string
  codes: string[]
}

function parseEntries(body: string): ParsedEntry[] {
  return body
    .split(';')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const match = entry.match(ENTRY)
      if (!match) return null
      const raw = match[2].toLowerCase()
      return {
        article: match[1],
        marketplace: normalizeMarketplace(raw),
        rawMarketplace: raw,
        sellerId: match[3],
        codes: splitReasonCodes(match[4]),
      }
    })
    .filter((entry): entry is ParsedEntry => entry !== null)
}

function groupEntries(entries: ParsedEntry[]): PipelineErrorGroup[] {
  const groups = new Map<string, PipelineErrorGroup>()
  for (const entry of entries) {
    for (const code of entry.codes) {
      const base = baseOf(code)
      const key = `${entry.marketplace ?? entry.rawMarketplace}|${base}`
      let group = groups.get(key)
      if (!group) {
        const summary = reasonSummary(code)
        group = {
          key,
          marketplace: entry.marketplace,
          marketplaceLabel: entry.marketplace
            ? MARKETPLACES[entry.marketplace].label
            : entry.rawMarketplace.toUpperCase(),
          code: base,
          short: summary.short,
          action: summary.action,
          articles: [],
          cabinets: [],
        }
        groups.set(key, group)
      }
      if (!group.articles.includes(entry.article)) group.articles.push(entry.article)
      if (!group.cabinets.includes(entry.sellerId)) group.cabinets.push(entry.sellerId)
    }
  }
  // Сначала то, что затронуло больше товаров.
  return [...groups.values()].sort((a, b) => b.articles.length - a.articles.length)
}

/** `MARKETPLACE_DRAFT_CARDINALITY_MISMATCH: expected=2, processed=0` и подобные служебные. */
function describeServiceCode(code: string, tail: string): { text: string; action: string | null } | null {
  if (code === 'MARKETPLACE_DRAFT_CARDINALITY_MISMATCH') {
    const expected = tail.match(/expected=(\d+)/)?.[1]
    const processed = tail.match(/processed=([^\s,]+)/)?.[1]
    const counts = expected && processed ? ` (ожидалось ${expected}, собрано ${processed})` : ''
    return {
      text: `Число собранных черновиков не совпало с планом${counts}`,
      action: 'Нажмите «Повторить шаг»; если расхождение останется, передайте технический текст разработчикам.',
    }
  }
  if (code === 'MARKETPLACE_DRAFT_RESPONSE_INVALID') {
    return {
      text: 'Сервис карточек вернул ответ в неожиданном формате',
      action: 'Нажмите «Повторить шаг»; если ошибка вернётся, передайте технический текст разработчикам.',
    }
  }
  return null
}

function readJsonError(value: string): string | null {
  if (!value.startsWith('{')) return null
  try {
    const parsed = JSON.parse(value)
    const message = String(parsed.error || parsed.message || '').trim()
    if (!message) return null
    const where = [
      parsed.cabinet ? `кабинет ${String(parsed.cabinet).toUpperCase()}` : '',
      parsed.article ? String(parsed.article) : '',
    ]
      .filter(Boolean)
      .join(', ')
    return where ? `${message} (${where})` : message
  } catch {
    return null
  }
}

/**
 * Разбор ошибки конвейера. Пустая строка → null. Нераспознанный текст
 * возвращается как `plain` без изменений: новая ошибка с бэкенда должна быть
 * видна, а не проглочена.
 */
export function parsePipelineError(raw: string | null | undefined): PipelineErrorView | null {
  const technical = String(raw ?? '').trim()
  if (!technical) return null

  let rest = technical
  let headline: string | null = null
  // Снимаем обёртки (`PIPELINE_DRAFT_PREPARATION_FAILED: …`).
  for (;;) {
    const match = rest.match(/^([A-Z][A-Z0-9_]{3,}):\s*/)
    if (!match || !(match[1] in WRAPPER_HEADLINES)) break
    headline = headline ?? WRAPPER_HEADLINES[match[1]]
    rest = rest.slice(match[0].length)
  }

  if (rest.startsWith(`${NOT_PUBLISHABLE}:`)) {
    const entries = parseEntries(rest.slice(NOT_PUBLISHABLE.length + 1))
    const groups = groupEntries(entries)
    if (groups.length > 0) {
      return {
        headline: headline ?? NOT_PUBLISHABLE_HEADLINE,
        groups,
        plain: null,
        plainAction: null,
        technical,
      }
    }
  }

  const json = readJsonError(rest)
  if (json) return { headline, groups: [], plain: json, plainAction: null, technical }

  const lead = rest.match(/^([A-Z][A-Z0-9_]{3,})(?::\s*([\s\S]*))?$/)
  if (lead) {
    const service = describeServiceCode(lead[1], lead[2] ?? '')
    if (service) {
      return { headline, groups: [], plain: service.text, plainAction: service.action, technical }
    }
    if (isKnownReasonCode(lead[1])) {
      const summary = reasonSummary(lead[1])
      const detail = lead[2] ? `: ${lead[2]}` : ''
      return {
        headline,
        groups: [],
        plain: `${summary.short.charAt(0).toUpperCase()}${summary.short.slice(1)}${detail}`,
        plainAction: summary.action,
        technical,
      }
    }
  }

  return {
    headline,
    groups: [],
    plain: rest === technical ? technical : rest,
    plainAction: null,
    technical,
  }
}

/** Подпись «8 товаров» с правильным склонением. */
export const productsCountLabel = (count: number): string => {
  const mod100 = count % 100
  const mod10 = count % 10
  if (mod100 >= 11 && mod100 <= 14) return `${count} товаров`
  if (mod10 === 1) return `${count} товар`
  if (mod10 >= 2 && mod10 <= 4) return `${count} товара`
  return `${count} товаров`
}

/** Полное название причины для подсказки (в отличие от краткой фразы группы). */
export const groupFullTitle = (group: PipelineErrorGroup): string => blockingReason(group.code).title
